import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, InputGroup, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FiEdit2, FiRefreshCw, FiSearch, FiTrash2, FiUserCheck, FiUsers } from 'react-icons/fi';
import axios from 'axios';

import Card from '../../../components/Card';
import { getCurrentTokenSource, startImpersonationSession, useAuth } from '../../../components/auth/AuthProvider';
import apiUrl from '../../../config/api';

const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

const getAuthHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
});

const normalizeAdminError = (requestError) => {
  return requestError.response?.data?.message || requestError.message || 'Errore durante il recupero degli utenti.';
};

const roleVariant = {
  'super-admin': 'danger',
  admin: 'warning',
  user: 'primary',
};

const roleOptions = ['user', 'admin', 'super-admin'];

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
};

const normalizeText = (value = '') => String(value)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const emptyEditForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: 'user',
};

const UserList = () => {
  const { userDetails } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [busyAction, setBusyAction] = useState('');

  const currentUserId = String(userDetails?.id || userDetails?._id || '');
  const isSuperAdmin = userDetails?.role === 'super-admin';

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.get(`${apiUrl}/admin/users`, {
        headers: getAuthHeaders(),
      });

      setUsers(Array.isArray(response.data.users) ? response.data.users : []);
    } catch (requestError) {
      setError(normalizeAdminError(requestError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const query = normalizeText(searchTerm.trim());

    if (!query) {
      return users;
    }

    return users.filter((user) => normalizeText([
      user.firstName,
      user.lastName,
      user.email,
      user.phone,
      user.role,
    ].join(' ')).includes(query));
  }, [searchTerm, users]);

  const roleCounts = useMemo(() => users.reduce((counts, user) => ({
    ...counts,
    [user.role]: (counts[user.role] || 0) + 1,
  }), {}), [users]);

  const openEditModal = (user) => {
    setFeedback(null);
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      phone: user.phone || '',
      role: user.role || 'user',
    });
  };

  const closeEditModal = () => {
    setEditingUser(null);
    setEditForm(emptyEditForm);
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
  };

  const saveUser = async (event) => {
    event.preventDefault();

    if (!editingUser) {
      return;
    }

    setBusyAction(`edit-${editingUser.id}`);
    setFeedback(null);

    try {
      const response = await axios.put(`${apiUrl}/admin/users/${editingUser.id}`, editForm, {
        headers: getAuthHeaders(),
      });

      setUsers(Array.isArray(response.data.users) ? response.data.users : []);
      setFeedback({ variant: 'success', message: 'Utente aggiornato.' });
      closeEditModal();
    } catch (requestError) {
      setFeedback({ variant: 'danger', message: requestError.response?.data?.message || requestError.message });
    } finally {
      setBusyAction('');
    }
  };

  const openDeleteModal = (user) => {
    setFeedback(null);
    setDeletingUser(user);
    setDeleteConfirmation('');
  };

  const closeDeleteModal = () => {
    setDeletingUser(null);
    setDeleteConfirmation('');
  };

  const canConfirmDelete = /^(elimina|delete)$/i.test(deleteConfirmation.trim());

  const deleteUser = async () => {
    if (!deletingUser || !canConfirmDelete) {
      return;
    }

    setBusyAction(`delete-${deletingUser.id}`);
    setFeedback(null);

    try {
      const response = await axios.delete(`${apiUrl}/admin/users/${deletingUser.id}`, {
        headers: getAuthHeaders(),
      });

      setUsers(Array.isArray(response.data.users) ? response.data.users : []);
      setFeedback({ variant: 'success', message: 'Utente eliminato definitivamente.' });
      closeDeleteModal();
    } catch (requestError) {
      setFeedback({ variant: 'danger', message: requestError.response?.data?.message || requestError.message });
    } finally {
      setBusyAction('');
    }
  };

  const impersonateUser = async (user) => {
    const returnToken = getToken();

    if (!returnToken) {
      setFeedback({ variant: 'danger', message: 'Sessione admin non trovata.' });
      return;
    }

    setBusyAction(`impersonate-${user.id}`);
    setFeedback(null);

    try {
      const response = await axios.post(`${apiUrl}/admin/users/${user.id}/impersonate`, {}, {
        headers: getAuthHeaders(),
      });

      startImpersonationSession({
        token: response.data.token,
        returnToken,
        returnStorage: getCurrentTokenSource(),
        targetUser: response.data.user,
      });

      window.location.assign('/dashboard');
    } catch (requestError) {
      setFeedback({ variant: 'danger', message: requestError.response?.data?.message || requestError.message });
      setBusyAction('');
    }
  };

  const canDeleteUser = (user) => isSuperAdmin && String(user.id) !== currentUserId;
  const canImpersonateUser = (user) => isSuperAdmin && String(user.id) !== currentUserId;
  const canEditRole = (user) => isSuperAdmin && String(user.id) !== currentUserId;

  return (
    <Row>
      <Col sm="12">
        <Card>
          <Card.Header className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="header-title">
              <h4 className="card-title mb-1">Utenti</h4>
              <p className="mb-0">Gestione reale degli account registrati.</p>
            </div>
            <Button variant="primary" className="d-inline-flex align-items-center gap-2" onClick={loadUsers} disabled={isLoading}>
              {isLoading ? <Spinner animation="border" size="sm" /> : <FiRefreshCw />}
              Aggiorna
            </Button>
          </Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
              <div className="d-flex flex-wrap gap-2">
                <Badge bg="primary" className="px-3 py-2">Totale: {users.length}</Badge>
                <Badge bg="danger" className="px-3 py-2">Super admin: {roleCounts['super-admin'] || 0}</Badge>
                <Badge bg="warning" text="dark" className="px-3 py-2">Admin: {roleCounts.admin || 0}</Badge>
                <Badge bg="secondary" className="px-3 py-2">User: {roleCounts.user || 0}</Badge>
              </div>
              <InputGroup className="admin-user-search">
                <InputGroup.Text>
                  <FiSearch />
                </InputGroup.Text>
                <Form.Control
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Cerca utente"
                />
              </InputGroup>
            </div>

            {error && (
              <Alert variant="danger">{error}</Alert>
            )}
            {feedback && (
              <Alert variant={feedback.variant} dismissible onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            <div className="table-responsive">
              <Table className="table table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th>Utente</th>
                    <th>Email</th>
                    <th>Telefono</th>
                    <th>Ruolo</th>
                    <th>Archivi</th>
                    <th>Materie</th>
                    <th>Domande</th>
                    <th>Quiz</th>
                    <th>Registrato</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="10" className="text-center py-5">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Caricamento utenti...
                      </td>
                    </tr>
                  ) : filteredUsers.length ? (
                    filteredUsers.map((user) => (
                      <tr key={user.id}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <span className="avatar-40 rounded bg-soft-primary d-inline-flex align-items-center justify-content-center">
                              <FiUsers />
                            </span>
                            <div>
                              <strong>{user.firstName} {user.lastName}</strong>
                              <div className="small text-muted">ID {user.id}</div>
                            </div>
                          </div>
                        </td>
                        <td>{user.email}</td>
                        <td>{user.phone || '-'}</td>
                        <td>
                          <Badge bg={roleVariant[user.role] || 'secondary'}>{user.role}</Badge>
                        </td>
                        <td>{user.stats?.archives || 0}</td>
                        <td>{user.stats?.subjects || 0}</td>
                        <td>{user.stats?.questions || 0}</td>
                        <td>{user.stats?.quizAttempts || 0}</td>
                        <td>{formatDate(user.createdAt)}</td>
                        <td>
                          <div className="d-flex align-items-center gap-1 list-user-action">
                            <Button
                              variant="warning"
                              size="sm"
                              className="btn-icon"
                              title="Modifica"
                              onClick={() => openEditModal(user)}
                              disabled={Boolean(busyAction)}
                            >
                              <span className="btn-inner"><FiEdit2 /></span>
                            </Button>
                            <Button
                              variant="info"
                              size="sm"
                              className="btn-icon"
                              title="Impersona"
                              onClick={() => impersonateUser(user)}
                              disabled={!canImpersonateUser(user) || Boolean(busyAction)}
                            >
                              <span className="btn-inner">
                                {busyAction === `impersonate-${user.id}` ? <Spinner animation="border" size="sm" /> : <FiUserCheck />}
                              </span>
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              className="btn-icon"
                              title="Elimina definitivamente"
                              onClick={() => openDeleteModal(user)}
                              disabled={!canDeleteUser(user) || Boolean(busyAction)}
                            >
                              <span className="btn-inner"><FiTrash2 /></span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-5">
                        Nessun utente trovato.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Modal show={Boolean(editingUser)} onHide={closeEditModal} centered>
        <Form onSubmit={saveUser}>
          <Modal.Header closeButton>
            <Modal.Title>Modifica utente</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md="6">
                <Form.Group className="form-group">
                  <Form.Label>Nome</Form.Label>
                  <Form.Control name="firstName" value={editForm.firstName} onChange={handleEditChange} required />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="form-group">
                  <Form.Label>Cognome</Form.Label>
                  <Form.Control name="lastName" value={editForm.lastName} onChange={handleEditChange} required />
                </Form.Group>
              </Col>
              <Col md="12">
                <Form.Group className="form-group">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" name="email" value={editForm.email} onChange={handleEditChange} required />
                </Form.Group>
              </Col>
              <Col md="12">
                <Form.Group className="form-group">
                  <Form.Label>Telefono</Form.Label>
                  <Form.Control name="phone" value={editForm.phone} onChange={handleEditChange} />
                </Form.Group>
              </Col>
              <Col md="12">
                <Form.Group className="form-group mb-0">
                  <Form.Label>Ruolo</Form.Label>
                  <Form.Select name="role" value={editForm.role} onChange={handleEditChange} disabled={!canEditRole(editingUser || {})}>
                    {roleOptions.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={closeEditModal}>Annulla</Button>
            <Button type="submit" variant="primary" disabled={busyAction === `edit-${editingUser?.id}`}>
              {busyAction === `edit-${editingUser?.id}` ? <Spinner animation="border" size="sm" /> : 'Salva'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(deletingUser)} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Elimina utente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            Questa azione elimina definitivamente l'utente e tutti i suoi dati collegati: archivi, materie, domande e quiz.
          </Alert>
          <p className="mb-2">
            Utente: <strong>{deletingUser?.firstName} {deletingUser?.lastName}</strong>
          </p>
          <Form.Group className="form-group mb-0">
            <Form.Label>Scrivi <strong>elimina</strong> oppure <strong>delete</strong> per confermare.</Form.Label>
            <Form.Control value={deleteConfirmation} onChange={(event) => setDeleteConfirmation(event.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={closeDeleteModal}>Annulla</Button>
          <Button variant="danger" onClick={deleteUser} disabled={!canConfirmDelete || busyAction === `delete-${deletingUser?.id}`}>
            {busyAction === `delete-${deletingUser?.id}` ? <Spinner animation="border" size="sm" /> : 'Elimina definitivamente'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export default UserList;
